import { DEG, formatNumber, clamp } from '../math.js';

const GRAVITY_EPS = 1e-9;

/** 沿斜面向下为正方向时，滑块的加速度。 */
export function inclineAcceleration(params, velocity = null) {
  const theta = params.angle * DEG;
  const g = params.gravity;
  const mu = params.friction;
  const gravityAlong = g * Math.sin(theta);
  const maxFriction = mu * g * Math.cos(theta);
  const v = velocity ?? 0;
  if (Math.abs(v) < GRAVITY_EPS) {
    return gravityAlong > maxFriction ? gravityAlong - maxFriction : 0;
  }
  return v > 0 ? gravityAlong - maxFriction : gravityAlong + maxFriction;
}

/** 静摩擦能否独自平衡重力沿斜面分量。 */
export function isStaticEquilibrium(params) {
  const theta = params.angle * DEG;
  return Math.tan(theta) <= params.friction + 1e-12;
}

function energyOf(state, params) {
  const theta = params.angle * DEG;
  const kinetic = 0.5 * params.mass * state.v * state.v;
  const height = (params.length - clamp(state.s, 0, params.length)) * Math.sin(theta);
  const potential = params.mass * params.gravity * height;
  return { kinetic, potential, total: kinetic + potential };
}

function predictedBottomSpeed(params) {
  if (params.initialSpeed === 0 && isStaticEquilibrium(params)) return null;
  const a = inclineAcceleration(params, params.initialSpeed || 1);
  const v2 = params.initialSpeed ** 2 + 2 * a * params.length;
  return v2 > 0 ? Math.sqrt(v2) : null;
}

export const incline = {
  id: 'incline',
  order: 3,
  meta: {
    title: '斜面滑块',
    shortTitle: '斜面',
    eyebrow: 'FORCES ON AN INCLINE',
    tagline: '把重力拆成两个方向',
    description:
      '调节斜面倾角、摩擦系数与释放速度，观察滑块沿斜面下滑的加速度、受力分解和摩擦生热，验证牛顿第二定律与能量守恒。',
    accent: '#7dd3fc',
    accentRgb: '125, 211, 252',
    grade: '高中物理 · 牛顿运动定律',
    duration: '建议 15 分钟',
    objectives: [
      '会对斜面上的物体进行受力分析，分解重力并写出沿斜面方向的牛顿第二定律方程。',
      '理解动摩擦因数、倾角与加速度的关系，会判断物体是否保持静止。',
      '利用运动学公式和能量观点分别求解滑块到达底端的速度。',
      '计算摩擦生热，理解机械能守恒的条件与耗散过程。',
    ],
    apparatus: ['可调倾角斜面', '滑块', '刻度尺', '数字计时器', '光电门', '天平', 'PhysLab 仿真终端'],
    theory: [
      '滑块受重力 $mg$、支持力 $N$ 与摩擦力 $f$。垂直斜面方向 $N=mg\\cos\\theta$，沿斜面方向取向下为正。',
      '滑动时 $f=\\mu N=\\mu mg\\cos\\theta$，加速度 $a=g(\\sin\\theta-\\mu\\cos\\theta)$。若 $\\tan\\theta\\le\\mu$，滑块可以静止在斜面上。',
      '由静止释放且能滑到底端时，$v^2=2aL$；一般情形 $v^2=v_0^2+2aL$。',
      '能量观点：$mgL\\sin\\theta+\\tfrac12mv_0^2=\\tfrac12mv^2+Q$，其中摩擦生热 $Q=fL=\\mu mgL\\cos\\theta$。',
      '若斜面光滑（$\\mu=0$），只有重力做功，机械能守恒；有摩擦时机械能减少，减少量等于克服摩擦力做的功。',
    ],
    procedure: [
      '用角度传感器或量角器把斜面调到目标倾角，记录 $\\theta$。',
      '用天平测滑块质量，用刻度尺测释放点到斜面底端的距离 $L$。',
      '由静止释放滑块，用光电门或计时器测量下滑时间，重复 3 次。',
      '由 $L=\\tfrac12at^2$ 求加速度，再由 $\\mu=(g\\sin\\theta-a)/(g\\cos\\theta)$ 求动摩擦因数。',
      '改变倾角或接触面材料，重复实验，绘制 $a-\\sin\\theta$ 图像。',
      '用能量观点计算摩擦生热，与 $\\mu mgL\\cos\\theta$ 对照。',
    ],
    safety: [
      '滑块下滑路径上不要放置手或其他物品。',
      '倾角较大时注意滑块速度，底端应设置缓冲挡板。',
      '测量摩擦因数时保持接触面清洁、干燥，避免偶然污染。',
    ],
    formulas: [
      { label: '支持力', latex: 'N=mg\\cos\\theta', note: '垂直斜面方向平衡' },
      { label: '加速度', latex: 'a=g(\\sin\\theta-\\mu\\cos\\theta)', note: '向下滑动时' },
      { label: '底端速度', latex: 'v=\\sqrt{v_0^2+2aL}', note: '匀加速直线运动' },
      { label: '摩擦生热', latex: 'Q=\\mu mgL\\cos\\theta', note: '等于克服摩擦力做功' },
    ],
    recordTable: {
      title: '斜面下滑加速度记录表',
      columns: ['次数', '倾角 θ / °', '质量 m / kg', '位移 L / m', '时间 t / s', '加速度 a / (m·s⁻²)', 'μ'],
      rows: 5,
    },
  },
  schema: [
    {
      key: 'angle',
      label: '斜面倾角',
      symbol: 'θ',
      unit: '°',
      min: 5,
      max: 60,
      step: 1,
      default: 30,
      precision: 0,
      group: '斜面几何',
      apply: 'live',
      description: '斜面与水平面的夹角。倾角越大，重力沿斜面的分量越大。',
    },
    {
      key: 'length',
      label: '斜面长度',
      symbol: 'L',
      unit: 'm',
      min: 1,
      max: 8,
      step: 0.1,
      default: 5,
      precision: 1,
      group: '斜面几何',
      apply: 'reset',
      description: '滑块从顶端滑到底端的位移大小。',
    },
    {
      key: 'mass',
      label: '滑块质量',
      symbol: 'm',
      unit: 'kg',
      min: 0.2,
      max: 5,
      step: 0.1,
      default: 1,
      precision: 1,
      group: '滑块参数',
      apply: 'live',
      description: '质量会影响摩擦力和能量，但在同一斜面上不改变加速度。',
    },
    {
      key: 'friction',
      label: '动摩擦因数',
      symbol: 'μ',
      unit: '',
      min: 0,
      max: 0.9,
      step: 0.01,
      default: 0.15,
      precision: 2,
      group: '接触面',
      apply: 'live',
      description: 'μ = 0 表示光滑斜面；tanθ ≤ μ 时滑块可保持静止。',
    },
    {
      key: 'gravity',
      label: '重力加速度',
      symbol: 'g',
      unit: 'm/s²',
      min: 1.6,
      max: 20,
      step: 0.1,
      default: 9.8,
      precision: 1,
      group: '环境参数',
      apply: 'live',
      description: '改变重力环境会影响下滑加速度与能量。',
    },
    {
      key: 'initialSpeed',
      label: '初速度',
      symbol: 'v₀',
      unit: 'm/s',
      min: 0,
      max: 5,
      step: 0.1,
      default: 0,
      precision: 1,
      group: '初始条件',
      apply: 'reset',
      description: '沿斜面向下的初速度，0 表示由静止释放。',
    },
  ],
  createState(params) {
    return {
      t: 0,
      s: 0,
      v: params.initialSpeed,
      distance: 0,
      status: 'sliding',
      impactSpeed: null,
    };
  },
  advance(state, params, dt) {
    if (state.status === 'bottom') return state;
    const L = params.length;
    const s0 = state.s;
    const v0 = state.v;
    const a = inclineAcceleration(params, v0);

    let s1 = s0 + v0 * dt + 0.5 * a * dt * dt;
    let v1 = v0 + a * dt;
    let tUsed = dt;

    // 速度换向：本步内先减速到 0；下一步再由静摩擦条件决定是否继续。
    if ((v0 > 0 && v1 < 0) || (v0 < 0 && v1 > 0)) {
      const stopTime = -v0 / a;
      s1 = s0 + v0 * stopTime + 0.5 * a * stopTime * stopTime;
      v1 = 0;
      tUsed = stopTime;
    }

    // 到达底端：保留到达瞬间的速度，结束数据采集。
    if (s1 >= L && v0 > 0) {
      const discriminant = v0 * v0 + 2 * a * (L - s0);
      let tHit;
      if (Math.abs(a) < 1e-12) {
        tHit = (L - s0) / v0;
      } else if (discriminant >= 0) {
        tHit = (-v0 + Math.sqrt(discriminant)) / a;
      } else {
        tHit = dt;
      }
      tHit = clamp(tHit, 0, dt);
      const vHit = Math.sqrt(Math.max(0, v0 * v0 + 2 * a * (L - s0)));
      return {
        t: state.t + tHit,
        s: L,
        v: vHit,
        distance: state.distance + (L - s0),
        status: 'bottom',
        impactSpeed: vHit,
      };
    }

    if (Math.abs(v1) < 1e-7) v1 = 0;
    return {
      t: state.t + tUsed,
      s: s1,
      v: v1,
      distance: state.distance + Math.abs(s1 - s0),
      status: state.status,
      impactSpeed: state.impactSpeed,
    };
  },
  measure(state, params) {
    const theta = params.angle * DEG;
    const energy = energyOf(state, params);
    const a = inclineAcceleration(params, state.v);
    const normalForce = params.mass * params.gravity * Math.cos(theta);
    const moving = Math.abs(state.v) > 1e-6;
    const staticHold = !moving && isStaticEquilibrium(params);
    const frictionMagnitude = moving
      ? params.friction * normalForce
      : staticHold
        ? params.mass * params.gravity * Math.sin(theta)
        : params.friction * normalForce;
    const frictionWork = params.friction * params.mass * params.gravity * Math.cos(theta) * state.distance;
    const predicted = predictedBottomSpeed(params);
    const status = state.status === 'bottom'
      ? { tone: 'ok', label: '到达底端', detail: '已停止采集，可重置后改变参数' }
      : staticHold
        ? { tone: 'info', label: '静摩擦平衡', detail: 'tanθ ≤ μ，滑块保持静止' }
        : { tone: 'info', label: '下滑中', detail: '沿斜面方向做匀加速运动' };

    return {
      time: state.t,
      energy,
      momentum: null,
      status,
      readouts: [
        { key: 's', label: '下滑位移', value: state.s, unit: 'm', precision: 2 },
        { key: 'speed', label: '速度', value: state.v, unit: 'm/s', precision: 2 },
        { key: 'accel', label: '加速度', value: a, unit: 'm/s²', precision: 2 },
        { key: 'time', label: '已用时间', value: state.t, unit: 's', precision: 2 },
        { key: 'frictionWork', label: '摩擦生热', value: frictionWork, unit: 'J', precision: 2 },
        {
          key: 'predictedBottom',
          label: '理论底端速度',
          value: predicted ?? Number.NaN,
          unit: 'm/s',
          precision: 2,
          hint: predicted === null ? '摩擦力过大，无法到达底端' : '由 v² = v₀² + 2aL 计算',
        },
      ],
      extras: {
        acceleration: a,
        normalForce,
        frictionMagnitude,
        frictionWork,
        gravityAlong: params.mass * params.gravity * Math.sin(theta),
        height: (params.length - state.s) * Math.sin(theta),
        predictedBottomSpeed: predicted,
        staticHold,
        distance: state.distance,
        angle: params.angle,
        impactSpeed: state.impactSpeed,
        theoreticalAcceleration: Math.max(0, params.gravity * (Math.sin(theta) - params.friction * Math.cos(theta))),
      },
    };
  },
  reference(state, params) {
    return { energy: energyOf(state, params).total, momentum: null };
  },
  conservation(params) {
    return { energy: params.friction > 0 ? 'dissipative' : 'conserved', momentum: null };
  },
  isFinished(state) {
    return state.status === 'bottom';
  },
  describe(params) {
    const a = Math.max(0, params.gravity * (Math.sin(params.angle * DEG) - params.friction * Math.cos(params.angle * DEG)));
    return `理论加速度 a = ${formatNumber(a, 2)} m/s²（tanθ ${isStaticEquilibrium(params) ? '≤' : '>'} μ）`;
  },
};
